import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import styles from "../Home/HomePage.module.css";
import * as XLSX from "xlsx";

export const authenticatedFetch = async (url, options = {}) => {
  const token =
    localStorage.getItem("auth_token") || localStorage.getItem("token");

  if (!token) {
    console.error("No auth token found, redirecting to login");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("token");
    localStorage.removeItem("profile");
    window.location.href = "/login";
    throw new Error("Authentication required");
  }

  const headers = {
    ...options.headers,
    Authorization: `Bearer ${token}`, // Keep Bearer for JWT compatibility
  };

  return fetch(url, { ...options, headers });
};

const handleAuthError = (response) => {
  if (response.status === 401) {
    // Token expired or invalid - redirect to login
    localStorage.removeItem("auth_token");
    localStorage.removeItem("profile");
    window.location.href = "/login";
    return true;
  }
  return false;
};

const readingDateTimeFormatter = new Intl.DateTimeFormat("en-ZA", {
  timeZone: "Africa/Johannesburg",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const formatReadingDateTime = (value) => {
  if (!value) return "—";

  const date = value instanceof Date ? value : new Date(value);

  if (!Number.isFinite(date.getTime())) {
    return "—";
  }

  return readingDateTimeFormatter.format(date);
};

export default function MeterDetailPage() {
  localStorage.setItem("navbar_state", "user");
  const { meterId } = useParams();

  const navigate = useNavigate();
  //const API = "http://127.0.0.1:8000/api";
  const API = "https://simplix.co.za/api";

  useEffect(() => {
    const token =
      localStorage.getItem("auth_token") || localStorage.getItem("token");
    const profile = localStorage.getItem("profile");

    if (!token || !profile) {
      navigate("/#/login", { replace: true });
      return;
    }

    load();
    loadManualReadingUsage();
  }, [meterId, navigate]);

  async function blockIfPendingAccounts() {
    const email = (
      localStorage.getItem("userEmail") ||
      JSON.parse(localStorage.getItem("profile") || "{}").email ||
      ""
    ).trim();

    if (!email) {
      navigate("/#/login", { replace: true });
      return true;
    }

    const r = await authenticatedFetch(`${API}/accounts`, {
      headers: { Accept: "application/json" },
    });

    if (r.status === 401 || r.status === 403) {
      localStorage.removeItem("auth_token");
      navigate("/#/login", { replace: true });
      return true;
    }

    if (!r.ok) return false;

    const j = await r.json();
    const accounts = Array.isArray(j?.accounts) ? j.accounts : [];

    const hasPending = accounts.some(
      (a) => String(a?.status || "").toLowerCase() !== "active",
    );

    if (hasPending) {
      sessionStorage.setItem(
        "profile_notice",
        "Please upload your ID document for your pending account before using meters, wallets or payments.",
      );

      navigate("/profile", { replace: true });
      return true;
    }

    return false;
  }

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [meter, setMeter] = useState(null);
  const [readings, setReadings] = useState([]);
  const [qNotes, setQNotes] = useState("");
  const [chartMode, setChartMode] = useState("usage"); // "usage" | "reading" | "daily"
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedDailyMonth, setSelectedDailyMonth] = useState("");
  const [walletBalance, setWalletBalance] = useState(null); // Rands
  const [avgSpendPerDayR, setAvgSpendPerDayR] = useState(null);
  const [tenantOpen, setTenantOpen] = useState(false);
  const [tenantBusy, setTenantBusy] = useState(false);
  const [tenantError, setTenantError] = useState("");
  const [tenantMsg, setTenantMsg] = useState("");
  const [tenantSearch, setTenantSearch] = useState("");
  const [tenantResults, setTenantResults] = useState([]);
  const [selectedTenantAccount, setSelectedTenantAccount] = useState(null);
  const [autofilledFields, setAutofilledFields] = useState({
  firstName: false,
  lastName: false,
  email: false,
  mobile: false,
});

  const [tenantForm, setTenantForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
  });

  // ===== Tariff (R/kWh) — now sourced from backend (flat rate only) =====
  const [rate, setRate] = useState(0);

  // ---- Take reading (manual, now) -------------------------------------------
  const [takeBusy, setTakeBusy] = useState(false);
  const [takeErr, setTakeErr] = useState("");
  const [takeMsg, setTakeMsg] = useState("");

  // === StronPower (relay/tamper) =============================================
  const [relayState, setRelayState] = useState(null); // "On" | "Off" | null
  const [tamperState, setTamperState] = useState(null); // "On" | "Off" | null
  const [spError, setSpError] = useState("");
  const [spLoading, setSpLoading] = useState(false);

  // current date as "YYYY-MM-DD" (local, not UTC)
  const todayStrLocal = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  // Call backend endpoint that wraps `call_stronpower_day(...)`.
  // It should accept: { meter_number, date } and return the example array.

  // ===========================================================================

  const CLIENT_READING_LIMIT = 5;

  const getUserProfileId = () => {
    try {
      const p = JSON.parse(localStorage.getItem("profile") || "{}");
      return (
        p?.id ||
        p?.userProfileId ||
        p?.user_profile_id ||
        p?.new_userprofileid ||
        localStorage.getItem("user_profile_id") ||
        ""
      );
    } catch {
      return localStorage.getItem("user_profile_id") || "";
    }
  };

  const [takeCount, setTakeCount] = useState(0);
  const [takeLimit, setTakeLimit] = useState(CLIENT_READING_LIMIT);

  async function loadManualReadingUsage() {
    const userProfileId = getUserProfileId();

    if (!userProfileId) {
      setTakeErr("Could not identify your user profile. Please log in again.");
      return;
    }

    const res = await authenticatedFetch(
      `${API}/meters/${meterId}/readings/manual-usage/`,
      {
        headers: { Accept: "application/json" },
      },
    );

    if (res.status === 401 || res.status === 403) {
      localStorage.removeItem("auth_token");
      navigate("/#/login", { replace: true });
      return;
    }
    const j = await res.json().catch(() => ({}));

    if (!res.ok) {
      setTakeErr(j?.error || "Could not load reading limit.");
      return;
    }

    setTakeCount(Number(j.used || 0));
    setTakeLimit(Number(j.limit || CLIENT_READING_LIMIT));
  }

  const cleanReadingNotes = (notes) => {
    const s = String(notes || "");

    if (s.includes("Client reading via portal.")) {
      const match = s.match(/Cumulative Total\s*=\s*([0-9.]+)/i);
      return match
        ? `Client reading via portal. Cumulative Total = ${match[1]} kWh`
        : "Client reading via portal";
    }

    return s;
  };

  async function load() {
    setLoading(true);
    setError("");

    try {
      const dRes = await authenticatedFetch(`${API}/meters/${meterId}/`, {
        headers: { Accept: "application/json" },
      });

      if (dRes.status === 401 || dRes.status === 403) {
        localStorage.removeItem("auth_token");
        navigate("/#/login", { replace: true });
        return;
      }
      if (!dRes.ok) {
        throw new Error("Failed to load meter");
      }

      const dJson = await dRes.json();
      const m = dJson?.meter || null;

      if (!m) {
        throw new Error("Meter was not returned");
      }

      setMeter(m);

      const eff = m.effectiveRateRPerKwh;
      const isTou = Boolean(m.isTimeOfUse);

      setRate(!isTou && Number.isFinite(eff) ? Number(eff) : 0);

      setSelectedYear(new Date().getFullYear());

      const rRes = await authenticatedFetch(
        `${API}/meters/${meterId}/readings/?limit=2000}`,
        {
          headers: { Accept: "application/json" },
        },
      );
      if (!rRes.ok) {
        throw new Error("Failed to load meter readings");
      }

      const rJson = await rRes.json();

      const arr = (rJson?.readings || [])
        .map((x) => ({
          id: x.id,
          value:
            typeof x.value === "number"
              ? x.value
              : x.value
                ? Number(x.value)
                : null,
          units: x.units || "",
          readingDate: x.readingDate ? new Date(x.readingDate) : null,
          notes: cleanReadingNotes(x.notes),
        }))
        .filter(
          (reading) =>
            Number.isFinite(reading.readingDate?.getTime()) &&
            Number.isFinite(reading.value),
        );

      arr.sort((first, second) => first.readingDate - second.readingDate);

      setReadings(arr);

      const latest = arr[arr.length - 1]?.readingDate;

      if (latest) {
        setSelectedYear(latest.getFullYear());
      }
    } catch (loadError) {
      console.error(loadError);
      setMeter(null);
      setError(
        "We could not load the meter information. Please refresh the page and try again.",
      );
    } finally {
      setLoading(false);
    }
  }

  function goToWalletFromMeter(meter) {
    const guid = (meter?.accountGuid || "").trim();
    if (!guid) return;

    navigate(`/accounts/${encodeURIComponent(guid)}/wallet`);
  }

  // Load StronPower status once we know the meter number

  const filtered = useMemo(() => {
    const t = qNotes.trim().toLowerCase();
    if (!t) return readings.slice().reverse(); // latest first in table
    return readings
      .filter((r) => (r.notes || "").toLowerCase().includes(t))
      .slice()
      .reverse();
  }, [qNotes, readings]);

  const years = useMemo(() => {
    const set = new Set(readings.map((r) => r.readingDate.getFullYear()));
    return Array.from(set).sort((a, b) => b - a);
  }, [readings]);

  // ---------------- Pagination (10 per page) ----------------
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [meterId, qNotes]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const rangeStart = filtered.length ? (page - 1) * PAGE_SIZE + 1 : 0;
  const rangeEnd = Math.min(filtered.length, page * PAGE_SIZE);

  const pageNumbers = useMemo(() => {
    const out = new Set([1, totalPages, page - 1, page, page + 1]);
    return [...out]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  }, [page, totalPages]);
  // ----------------------------------------------------------

  // series for raw points
  const readingSeries = useMemo(
    () => readings.map((r) => ({ x: r.readingDate, y: r.value })),
    [readings],
  );

  // usage deltas (>=0)
  const usagePoints = useMemo(() => {
    if (readings.length < 2) return [];
    const out = [];
    for (let i = 1; i < readings.length; i++) {
      const prev = readings[i - 1];
      const curr = readings[i];
      const delta = Math.max(0, curr.value - prev.value);
      out.push({ x: curr.readingDate, y: delta });
    }
    return out;
  }, [readings]);

  // ===== NEW: per-reading consumption (newer - previous) =====================
  const consumptionById = useMemo(() => {
    // readings are ascending; map consumption to the NEWER reading's id
    const m = new Map();
    for (let i = 1; i < readings.length; i++) {
      const delta = Math.max(0, readings[i].value - readings[i - 1].value);
      m.set(readings[i].id, delta);
    }
    return m;
  }, [readings]);
  // ==========================================================================

  // ===== Helpers for grouping/averages =======================================
  const startOfDay = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const isoWeekKey = (d) => {
    // ISO week: Thursday-based week number.
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const day = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
    return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
  };
  const monthBucket = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

  // Aggregate usage by day/week/month from usagePoints (each point is a delta on that date)
  const dayMap = useMemo(() => {
    const m = new Map();
    for (const p of usagePoints) {
      const k = startOfDay(p.x).toISOString().slice(0, 10);
      m.set(k, (m.get(k) || 0) + p.y);
    }
    return m;
  }, [usagePoints]);

  const weekMap = useMemo(() => {
    const m = new Map();
    for (const p of usagePoints) {
      const k = isoWeekKey(p.x);
      m.set(k, (m.get(k) || 0) + p.y);
    }
    return m;
  }, [usagePoints]);

  const monthMap = useMemo(() => {
    const m = new Map();
    for (const p of usagePoints) {
      const k = monthBucket(p.x);
      m.set(k, (m.get(k) || 0) + p.y);
    }
    return m;
  }, [usagePoints]);

  // Daily average over last 30 days
  const dailyAvgKwh = useMemo(() => {
    if (!usagePoints.length) return 0;

    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - 29);

    const pts = usagePoints.filter((p) => p.x >= start && p.x <= now);

    if (!pts.length) return 0;

    const total = pts.reduce((a, b) => a + b.y, 0);

    const sorted = [...pts].sort((a, b) => a.x - b.x);

    const firstDate = sorted[0].x;

    const actualDays =
      Math.floor(
        (now.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24),
      ) + 1;

    const effectiveDays = Math.min(Math.max(actualDays, 1), 30);

    return total / effectiveDays;
  }, [usagePoints]);

  // Consistent averages derived from daily average
  const weeklyAvgKwh = useMemo(() => dailyAvgKwh * 7, [dailyAvgKwh]);

  const monthlyAvgKwh = useMemo(() => dailyAvgKwh * 30.4375, [dailyAvgKwh]);

  // Credits (R) using BACKEND rate (0 if TOU)
  const dailyAvgR = useMemo(() => dailyAvgKwh * rate, [dailyAvgKwh, rate]);
  const weeklyAvgR = useMemo(() => weeklyAvgKwh * rate, [weeklyAvgKwh, rate]);
  const monthlyAvgR = useMemo(
    () => monthlyAvgKwh * rate,
    [monthlyAvgKwh, rate],
  );

  // Current balance (kWh) — from backend remainingKwh
  const currentBalanceKwh = useMemo(() => {
    const v = meter?.remainingKwh;
    return Number.isFinite(v) ? Math.max(0, Number(v)) : 0;
  }, [meter]);

  const currentBalanceRands = useMemo(
    () => currentBalanceKwh * rate,
    [currentBalanceKwh, rate],
  );

  const fmtR = (v) => `R${(Number(v) || 0).toFixed(2)}`;
  const fmtK = (v) => `${(Number(v) || 0).toFixed(2)} kWh`;

  const humanizeDays = (days) => {
    if (!Number.isFinite(days)) return "—";
    if (days < 1) {
      const hrs = Math.floor(days * 24);
      return `${hrs} hr${hrs !== 1 ? "s" : ""}`;
    }
    if (days < 14) {
      const d = Math.floor(days);
      const hrs = Math.floor((days - d) * 24);
      return `${d} day${d !== 1 ? "s" : ""}${
        hrs > 0 ? ` ${hrs} hr${hrs !== 1 ? "s" : ""}` : ""
      }`;
    }
    const weeks = days / 7;
    if (weeks < 8) {
      return `${weeks.toFixed(1)} weeks`;
    }
    const months = days / 30.4375; // average month
    return `${months.toFixed(1)} months`;
  };

  // ---- Monthly aggregation for selected year --------------------------------
  // Sum *consumption* for each reading in the month.
  const monthlyUsage = useMemo(() => {
    if (!selectedYear) return [];

    const bins = Array(12).fill(null);

    for (const r of readings) {
      if (!r.readingDate || r.readingDate.getFullYear() !== selectedYear) {
        continue;
      }

      const consumption = consumptionById.get(r.id);

      if (Number.isFinite(consumption)) {
        const month = r.readingDate.getMonth();
        bins[month] = (bins[month] ?? 0) + consumption;
      }
    }

    return bins.map((value, month) => ({
      x: new Date(selectedYear, month, 15),
      y: value,
    }));
  }, [readings, selectedYear, consumptionById]);

  // For monthly "reading": last reading value within that month (snapshot)
  const dailyReadings = useMemo(
    () =>
      readings.filter((reading) =>
        String(reading.notes || "")
          .trim()
          .toLowerCase()
          .startsWith("daily reading"),
      ),
    [readings],
  );
  const availableDailyMonths = useMemo(() => {
    const months = new Map();

    for (const reading of dailyReadings) {
      const timestamp = reading.readingDate;

      if (!timestamp || timestamp > new Date()) {
        continue;
      }

      const year = timestamp.getFullYear();
      const month = timestamp.getMonth();
      const key = `${year}-${String(month + 1).padStart(2, "0")}`;

      if (!months.has(key)) {
        months.set(key, {
          key,
          year,
          month,
          label: new Intl.DateTimeFormat("en-ZA", {
            month: "long",
            year: "numeric",
          }).format(new Date(year, month, 1)),
        });
      }
    }

    return [...months.values()].sort(
      (a, b) => b.year - a.year || b.month - a.month,
    );
  }, [dailyReadings]);

  const activeDailyMonth =
    availableDailyMonths.find((month) => month.key === selectedDailyMonth) ||
    availableDailyMonths[0] ||
    null;

  const currentMonthDailyConsumption = useMemo(() => {
    if (!activeDailyMonth) return [];

    // Keep only the final automated daily reading for each day.
    // Client/manual readings are excluded before calculating consumption.
    const latestDailyReadingByDay = new Map();

    for (const reading of dailyReadings) {
      const timestamp = reading.readingDate;

      if (
        !timestamp ||
        timestamp.getFullYear() !== activeDailyMonth.year ||
        timestamp.getMonth() !== activeDailyMonth.month
      ) {
        continue;
      }

      const day = timestamp.getDate();
      const existing = latestDailyReadingByDay.get(day);

      if (!existing || timestamp > existing.readingDate) {
        latestDailyReadingByDay.set(day, reading);
      }
    }

    const monthDailyReadings = [...latestDailyReadingByDay.values()].sort(
      (a, b) => a.readingDate - b.readingDate,
    );

    return monthDailyReadings
      .map((reading, index) => {
        const previousDailyReading = monthDailyReadings[index - 1];

        if (!previousDailyReading) {
          return null;
        }

        return {
          x: reading.readingDate,
          y: Math.max(0, reading.value - previousDailyReading.value),
        };
      })
      .filter(Boolean);
  }, [dailyReadings, activeDailyMonth]);

  const dailyConsumptionExtremes = useMemo(() => {
    if (!currentMonthDailyConsumption.length) {
      return { highest: null, lowest: null };
    }

    return currentMonthDailyConsumption.reduce(
      (result, point) => ({
        highest:
          !result.highest || point.y > result.highest.y
            ? point
            : result.highest,
        lowest:
          !result.lowest || point.y < result.lowest.y ? point : result.lowest,
      }),
      { highest: null, lowest: null },
    );
  }, [currentMonthDailyConsumption]);

  const currentMonthLabel = activeDailyMonth?.label || "Daily Reading";

  const units = readings[0]?.units || "";

  // ---- Brand-styled bar chart (quantiles) -----------------------------------
  const BarChart = ({ data, height = 260, padding = 28 }) => {
    if (!data || data.length === 0) {
      return <div className="sub">Not enough data to chart.</div>;
    }

    // ensure we always have 12 months (fill missing with y=null)
    const months12 = [...Array(12)].map(
      (_, i) => data[i] ?? { x: new Date(selectedYear, i, 15), y: null },
    );

    const xs = months12.map((d) => d.x.getTime());
    const ys = months12.map((d) => (d.y == null ? null : Number(d.y)));
    const finiteYs = ys.filter((v) => v != null && Number.isFinite(v));

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    let minY = finiteYs.length ? Math.min(...finiteYs) : 0;
    let maxY = finiteYs.length ? Math.max(...finiteYs) : 1;

    if (maxY === minY) {
      minY = 0;
      maxY = maxY || 1;
    }

    const q = (p) => {
      const s = finiteYs.slice().sort((a, b) => a - b);
      if (s.length === 0) return 0;
      if (s.length === 1) return s[0];
      const idx = (s.length - 1) * p;
      const lo = Math.floor(idx);
      const hi = Math.ceil(idx);
      if (lo === hi) return s[lo];
      return s[lo] + (s[hi] - s[lo]) * (idx - lo);
    };
    const q33 = q(0.33);
    const q66 = q(0.66);

    const fillFor = (v) => {
      if (v == null) return "#e5e7eb";
      if (v <= q33) return "url(#usage-low-gradient)";
      if (v >= q66) return "url(#usage-peak-gradient)";
      return "url(#usage-middle-gradient)";
    };

    const svgWidth = 880;
    const svgHeight = height;
    const w = svgWidth - padding * 2;
    const h = svgHeight - padding * 2;

    const sx = (t) =>
      maxX === minX
        ? padding + w / 2
        : padding + ((t - minX) / (maxX - minX)) * w;

    const sy = (v) => padding + (1 - (v - minY) / (maxY - minY)) * h;

    const baselineY = sy(minY);
    const centers = xs.map((t) => sx(t));
    const bars = centers.map((cx, i) => {
      const prev =
        centers[i - 1] ?? cx - (centers[i + 1] ? centers[i + 1] - cx : w / 12);
      const next =
        centers[i + 1] ?? cx + (centers[i - 1] ? cx - centers[i - 1] : w / 12);
      const left = i === 0 ? cx - (next - cx) / 2 : (prev + cx) / 2;
      const right =
        i === centers.length - 1 ? cx + (cx - prev) / 2 : (cx + next) / 2;
      const gap = 4;
      const x = Math.max(padding, left + gap);
      const width = Math.max(
        4,
        Math.min(right - left - gap * 2, svgWidth - padding - x),
      );
      const v = ys[i];
      const yTop = v == null ? baselineY : sy(v);
      const height = Math.max(0, baselineY - yTop);
      return { x, y: yTop, width, height, v, monthIndex: i };
    });

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const tickIdx = [0, 3, 6, 9, 11];
    const yTickVals = [minY, (minY + maxY) / 2, maxY];

    return (
      <div className={styles.chartWrap}>
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className={styles.chartSvg}
          style={{ height: `${svgHeight}px` }}
          role="img"
          aria-label={`Monthly usage for ${selectedYear}`}
        >
          <defs>
            <linearGradient id="usage-low-gradient" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#0c1830" />
              <stop offset="100%" stopColor="#497bbe" />
            </linearGradient>
            <linearGradient
              id="usage-middle-gradient"
              x1="0"
              y1="1"
              x2="0"
              y2="0"
            >
              <stop offset="0%" stopColor="#0a579e" />
              <stop offset="100%" stopColor="#f68520" />
            </linearGradient>
            <linearGradient
              id="usage-peak-gradient"
              x1="0"
              y1="1"
              x2="0"
              y2="0"
            >
              <stop offset="0%" stopColor="#e14127" />
              <stop offset="100%" stopColor="#f68520" />
            </linearGradient>
          </defs>
          <line
            x1={padding}
            y1={svgHeight - padding}
            x2={svgWidth - padding}
            y2={svgHeight - padding}
            stroke="#e5e7eb"
          />
          <line
            x1={padding}
            y1={padding}
            x2={padding}
            y2={svgHeight - padding}
            stroke="#e5e7eb"
          />

          {yTickVals.map((v, i) => (
            <g key={i}>
              <line
                x1={padding}
                y1={sy(v)}
                x2={svgWidth - padding}
                y2={sy(v)}
                stroke="#f3f4f6"
              />
              <text
                x={padding - 8}
                y={sy(v)}
                textAnchor="end"
                alignmentBaseline="middle"
                fontSize="10"
                fill="#6b7280"
              >
                {Number.isFinite(v) ? v.toFixed(0) : ""}
              </text>
            </g>
          ))}

          {tickIdx.map((i) => (
            <g key={i}>
              <line
                x1={centers[i]}
                y1={svgHeight - padding}
                x2={centers[i]}
                y2={svgHeight - padding + 4}
                stroke="#9ca3af"
              />
              <text
                x={centers[i]}
                y={svgHeight - padding + 16}
                textAnchor="middle"
                fontSize="10"
                fill="#6b7280"
              >
                {monthNames[i]}
              </text>
            </g>
          ))}

          {bars.map((b, i) => (
            <rect
              key={i}
              x={b.x}
              y={b.y}
              width={b.width}
              height={b.height}
              fill={fillFor(b.v)}
              rx="5"
            >
              {b.v != null && (
                <title>
                  {`${monthNames[b.monthIndex]} ${selectedYear}: ${b.v.toLocaleString(
                    "en-ZA",
                    { maximumFractionDigits: 2 },
                  )} ${units}`}
                </title>
              )}
            </rect>
          ))}
        </svg>

        <div className={styles.chartLegend}>
          <span
            className="sub"
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                background: "linear-gradient(135deg, #0c1830, #497bbe)",
                borderRadius: 2,
              }}
            ></span>
            Low
          </span>
          <span
            className="sub"
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                background: "linear-gradient(135deg, #0a579e, #f68520)",
                borderRadius: 2,
              }}
            ></span>
            Middle
          </span>
          <span
            className="sub"
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                background: "linear-gradient(135deg, #e14127, #f68520)",
                borderRadius: 2,
              }}
            ></span>
            Peak
          </span>
          <span
            className="sub"
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                background: "#e5e7eb",
                borderRadius: 2,
              }}
            ></span>
            No data
          </span>
        </div>
      </div>
    );
  };
  // ---------------------------------------------------------------------------

  // ================== Accumulative Reading (Line) ============================
  const accumulativeSeries = useMemo(() => {
    if (!selectedYear) return { curr: [], prev: [] };

    const makeMonthlyTotalsForYear = (year) => {
      const totals = Array(12).fill(null);

      for (const point of usagePoints) {
        if (point.x.getFullYear() === year) {
          const month = point.x.getMonth();
          totals[month] = (totals[month] ?? 0) + point.y;
        }
      }

      return totals;
    };

    const toCumulative = (totals, year) => {
      let runningTotal = 0;

      return totals.map((value, month) => {
        if (!Number.isFinite(value)) {
          return {
            x: new Date(year, month, 15),
            y: null,
          };
        }

        runningTotal += value;

        return {
          x: new Date(year, month, 15),
          y: runningTotal,
        };
      });
    };

    return {
      curr: toCumulative(makeMonthlyTotalsForYear(selectedYear), selectedYear),
      prev: toCumulative(
        makeMonthlyTotalsForYear(selectedYear - 1),
        selectedYear - 1,
      ),
    };
  }, [usagePoints, selectedYear]);

  const LineChart = ({
    dataA,
    dataB,
    labelA,
    labelB,
    height = 260,
    padding = 28,
  }) => {
    const months12 = [...Array(12)].map(
      (_, i) =>
        dataA[i] ?? {
          x: new Date(selectedYear, i, 15),
          y: null,
        },
    );

    const months12B = [...Array(12)].map(
      (_, i) =>
        dataB[i] ?? {
          x: new Date(selectedYear - 1, i, 15),
          y: null,
        },
    );

    const xs = months12.map((d) => d.x.getTime());

    const ys = months12.map((d) =>
      d.y == null || !Number.isFinite(Number(d.y)) ? null : Number(d.y),
    );

    const ysB = months12B.map((d) =>
      d.y == null || !Number.isFinite(Number(d.y)) ? null : Number(d.y),
    );

    const finiteYs = [...ys, ...ysB].filter(Number.isFinite);

    if (!finiteYs.length) {
      return <div className={styles.chartEmpty}>No readings to chart.</div>;
    }

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = 0;
    const maxY = Math.max(1, Math.max(...finiteYs) * 1.08);

    const svgWidth = 880;
    const svgHeight = height;
    const w = svgWidth - padding * 2;
    const h = svgHeight - padding * 2;

    const sx = (t) =>
      maxX === minX
        ? padding + w / 2
        : padding + ((t - minX) / (maxX - minX)) * w;

    const sy = (v) => padding + (1 - (v - minY) / (maxY - minY)) * h;

    const centers = xs.map((t) => sx(t));
    const centersB = centers; // align months

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const tickIdx = [0, 3, 6, 9, 11];
    const yTickVals = [minY, (minY + maxY) / 2, maxY];

    const pathFor = (cxs, vals) => {
      let drawing = false;

      return vals
        .map((value, index) => {
          if (!Number.isFinite(value)) {
            drawing = false;
            return "";
          }

          const command = drawing ? "L" : "M";
          drawing = true;

          return `${command} ${cxs[index]} ${sy(value)}`;
        })
        .filter(Boolean)
        .join(" ");
    };
    const pathA = pathFor(centers, ys);
    const pathB = pathFor(centersB, ysB);

    return (
      <div className={styles.chartWrap}>
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className={styles.chartSvg}
          style={{ height: `${svgHeight}px` }}
          role="img"
          aria-label={`Accumulative readings for ${labelA} and ${labelB}`}
        >
          <defs>
            <linearGradient
              id="reading-current-gradient"
              x1="0"
              y1="0"
              x2="1"
              y2="0"
            >
              <stop offset="0%" stopColor="#0a579e" />
              <stop offset="55%" stopColor="#497bbe" />
              <stop offset="100%" stopColor="#e14127" />
            </linearGradient>
            <linearGradient
              id="reading-previous-gradient"
              x1="0"
              y1="0"
              x2="1"
              y2="0"
            >
              <stop offset="0%" stopColor="#497bbe" stopOpacity="0.72" />
              <stop offset="100%" stopColor="#f68520" stopOpacity="0.82" />
            </linearGradient>
          </defs>
          <line
            x1={padding}
            y1={svgHeight - padding}
            x2={svgWidth - padding}
            y2={svgHeight - padding}
            stroke="#e5e7eb"
          />
          <line
            x1={padding}
            y1={padding}
            x2={padding}
            y2={svgHeight - padding}
            stroke="#e5e7eb"
          />

          {yTickVals.map((v, i) => (
            <g key={i}>
              <line
                x1={padding}
                y1={sy(v)}
                x2={svgWidth - padding}
                y2={sy(v)}
                stroke="#f3f4f6"
              />
              <text
                x={padding - 8}
                y={sy(v)}
                textAnchor="end"
                alignmentBaseline="middle"
                fontSize="10"
                fill="#6b7280"
              >
                {Number.isFinite(v) ? v.toFixed(0) : ""}
              </text>
            </g>
          ))}

          {tickIdx.map((i) => (
            <g key={i}>
              <line
                x1={centers[i]}
                y1={svgHeight - padding}
                x2={centers[i]}
                y2={svgHeight - padding + 4}
                stroke="#9ca3af"
              />
              <text
                x={centers[i]}
                y={svgHeight - padding + 16}
                textAnchor="middle"
                fontSize="10"
                fill="#6b7280"
              >
                {monthNames[i]}
              </text>
            </g>
          ))}

          {/* Previous year is dashed; current year is solid. */}
          <path
            d={pathB}
            fill="none"
            stroke="url(#reading-previous-gradient)"
            strokeWidth="2.25"
            strokeDasharray="7 6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          <path
            d={pathA}
            fill="none"
            stroke="url(#reading-current-gradient)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {ysB.map((value, index) =>
            Number.isFinite(value) ? (
              <circle
                key={`pb-${index}`}
                cx={centersB[index]}
                cy={sy(value)}
                r="3.5"
                fill="#fff"
                stroke="#f68520"
                strokeWidth="2"
              >
                <title>
                  {`${monthNames[index]} ${labelB}: ${value.toLocaleString(
                    "en-ZA",
                    { maximumFractionDigits: 2 },
                  )} ${units}`}
                </title>
              </circle>
            ) : null,
          )}

          {ys.map((value, index) =>
            Number.isFinite(value) ? (
              <circle
                key={`pa-${index}`}
                cx={centers[index]}
                cy={sy(value)}
                r="4"
                fill="#fff"
                stroke="#0a579e"
                strokeWidth="2.5"
              >
                <title>
                  {`${monthNames[index]} ${labelA}: ${value.toLocaleString(
                    "en-ZA",
                    { maximumFractionDigits: 2 },
                  )} ${units}`}
                </title>
              </circle>
            ) : null,
          )}
        </svg>

        <div className={styles.chartLegend}>
          <span
            className="sub"
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <span
              style={{
                display: "inline-block",
                width: 14,
                height: 3,
                background: "linear-gradient(90deg, #0a579e, #497bbe, #e14127)",
                borderRadius: 2,
              }}
            ></span>
            {labelA}
          </span>
          <span
            className="sub"
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <span
              style={{
                display: "inline-block",
                width: 14,
                height: 3,
                background: "linear-gradient(90deg, #497bbe, #f68520)",
                borderRadius: 2,
              }}
            ></span>
            {labelB}
          </span>
        </div>
      </div>
    );
  };

  const DailyConsumptionLineChart = ({ data, height = 280, padding = 42 }) => {
    if (!data.length) {
      return (
        <div className={styles.chartEmpty}>
          No daily-reading consumption is available for the current month.
        </div>
      );
    }

    const now = new Date();

    const daysInMonth = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0,
    ).getDate();

    const values = Array(daysInMonth).fill(null);

    for (const point of data) {
      values[point.x.getDate() - 1] = Number(point.y);
    }

    const finiteValues = values.filter(Number.isFinite);
    const rawMax = Math.max(...finiteValues);
    const minY = 0;
    const maxY = Math.max(1, rawMax * 1.12);

    const svgWidth = 880;
    const svgHeight = height;
    const width = svgWidth - padding * 2;
    const chartHeight = svgHeight - padding * 2;

    const sx = (day) =>
      padding + ((day - 1) / Math.max(1, daysInMonth - 1)) * width;

    const sy = (value) =>
      padding + (1 - (value - minY) / Math.max(1, maxY - minY)) * chartHeight;

    const yTicks = [
      minY,
      minY + (maxY - minY) / 3,
      minY + ((maxY - minY) * 2) / 3,
      maxY,
    ];

    const xTicks = [...new Set([1, 5, 10, 15, 20, 25, daysInMonth])].filter(
      (day) => day <= daysInMonth,
    );

    let drawing = false;

    const path = values
      .map((value, index) => {
        if (!Number.isFinite(value)) {
          drawing = false;
          return "";
        }

        const command = drawing ? "L" : "M";
        drawing = true;

        return `${command} ${sx(index + 1)} ${sy(value)}`;
      })
      .filter(Boolean)
      .join(" ");

    return (
      <div className={styles.chartWrap}>
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className={styles.chartSvg}
          role="img"
          aria-label={`Daily consumption for ${currentMonthLabel}`}
        >
          <defs>
            <linearGradient id="daily-reading-line" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#0a579e" />
              <stop offset="100%" stopColor="#e14127" />
            </linearGradient>
          </defs>

          {yTicks.map((value, index) => (
            <g key={index}>
              <line
                x1={padding}
                y1={sy(value)}
                x2={svgWidth - padding}
                y2={sy(value)}
                stroke="#e9eef5"
              />

              <text
                x={padding - 8}
                y={sy(value)}
                textAnchor="end"
                alignmentBaseline="middle"
                fontSize="11"
                fill="#6b7280"
              >
                {value.toLocaleString("en-ZA", {
                  maximumFractionDigits: 1,
                })}
              </text>
            </g>
          ))}

          {xTicks.map((day) => (
            <g key={day}>
              <line
                x1={sx(day)}
                y1={padding}
                x2={sx(day)}
                y2={svgHeight - padding}
                stroke="#f2f5f9"
              />

              <text
                x={sx(day)}
                y={svgHeight - padding + 18}
                textAnchor="middle"
                fontSize="11"
                fill="#6b7280"
              >
                {day}
              </text>
            </g>
          ))}

          <path
            d={path}
            fill="none"
            stroke="url(#daily-reading-line)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {values.map((value, index) =>
            Number.isFinite(value) ? (
              <circle
                key={index}
                cx={sx(index + 1)}
                cy={sy(value)}
                r="4"
                fill="#fff"
                stroke="#0a579e"
                strokeWidth="2.5"
              >
                <title>
                  {`${index + 1} ${currentMonthLabel}: ${value.toLocaleString(
                    "en-ZA",
                    { maximumFractionDigits: 2 },
                  )} ${units}`}
                </title>
              </circle>
            ) : null,
          )}
        </svg>

        <div className={styles.chartLegend}>
          Daily Reading consumption · {data.length} day
          {data.length === 1 ? "" : "s"} available
        </div>
      </div>
    );
  };
  // ===========================================================================

  // ---- Pay button visibility based on meter.sgc -----------------------------
  const showPay = useMemo(() => {
    const sgc = (meter?.sgc ?? "").toString().toLowerCase();
    if (!sgc) return false;
    if (sgc.includes("broham")) return false;
    return sgc.includes("stron");
  }, [meter]);
  // ---------------------------------------------------------------------------
  const meterProvider = useMemo(() => {
    const sgc = (meter?.sgc ?? "").toString().toLowerCase();
    if (!sgc) return null;
    if (sgc.includes("broham")) return "broham";
    if (sgc.includes("stron") || sgc.includes("p4power")) return "p4power";
    return null;
  }, [meter]);

  const availableWalletCreditR = useMemo(() => {
    // In BC: negative balance = credit/good
    if (meterProvider !== "p4power") return 0;
    if (walletBalance == null) return 0;

    return Number(walletBalance) < 0 ? Math.abs(Number(walletBalance)) : 0;
  }, [meterProvider, walletBalance]);

  const estimatedRemainingKwh = useMemo(() => {
    // Broham already has kWh from backend
    if (meterProvider === "broham") return currentBalanceKwh;

    // P4Power wallet balance converted to kWh using tariff
    if (meterProvider === "p4power") {
      if (!rate || rate <= 0) return 0;
      return availableWalletCreditR / rate;
    }

    return 0;
  }, [meterProvider, currentBalanceKwh, availableWalletCreditR, rate]);

  const estimatedDaysLeft = useMemo(() => {
    if (meterProvider !== "p4power") {
      if (!dailyAvgKwh || dailyAvgKwh <= 0) return Infinity;
      if (estimatedRemainingKwh <= 0) return 0;

      return estimatedRemainingKwh / dailyAvgKwh;
    }

    const currentBalance = Number(walletBalance || 0);

    // Same logic as Wallet Page and Meter List:
    // In BC, negative balance = credit/good.
    if (currentBalance >= 0) {
      return 0;
    }

    if (!avgSpendPerDayR || avgSpendPerDayR <= 0) {
      return Infinity;
    }

    return Math.abs(currentBalance) / avgSpendPerDayR;
  }, [
    meterProvider,
    walletBalance,
    avgSpendPerDayR,
    dailyAvgKwh,
    estimatedRemainingKwh,
  ]);

  const estimatedDaysLeftFloor = useMemo(() => {
    return Number.isFinite(estimatedDaysLeft)
      ? Math.floor(estimatedDaysLeft)
      : null;
  }, [estimatedDaysLeft]);

  const needsAttention = useMemo(() => {
    return (
      Number(walletBalance || 0) >= 0 ||
      (estimatedDaysLeftFloor != null && estimatedDaysLeftFloor <= 10)
    );
  }, [walletBalance, estimatedDaysLeftFloor]);

  const displayBalance = useMemo(() => {
    if (meterProvider === "broham") {
      return {
        label: "Current balance",
        value: fmtK(currentBalanceKwh),
      };
    }

    if (meterProvider === "p4power") {
      return {
        label: "Wallet balance",
        value: walletBalance != null ? fmtR(walletBalance) : "—",
      };
    }

    return null;
  }, [meterProvider, currentBalanceKwh, walletBalance]);

  const storedProfile = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("profile") || "{}");
    } catch {
      return {};
    }
  }, []);
  const isOwnerProfile = useMemo(() => {
    const profileData = JSON.parse(localStorage.getItem("profile") || "{}");

    if (!profileData) return false;

    const typeLabel = profileData?.typeOfUserLabel;
    const typeValue = profileData?.new_typeofuser || profileData?.typeOfUser;

    const typeCheck = String(typeLabel || typeValue || "")
      .trim()
      .toLowerCase();

    return typeCheck === "owner" || Number(typeValue) === 100000000;
  }, []);

  const OWNER_TENANT_ALWAYS_ALLOWED = 100000000;
  const OWNER_TENANT_BALANCE_DEPENDENT = 100000001;
  const OWNER_TENANT_NEVER_ALLOWED = 100000002;

  const tenantManagementRule = Number(
    meter?.ownerTenantManagementRule ?? OWNER_TENANT_BALANCE_DEPENDENT,
  );

  const meterIsSettledOrInCredit = useMemo(() => {
    if (meterProvider === "p4power") {
      return walletBalance != null && Number(walletBalance) <= 0;
    }

    return Number(currentBalanceKwh || 0) >= 0;
  }, [meterProvider, walletBalance, currentBalanceKwh]);

  const canManageTenant = useMemo(() => {
    if (!isOwnerProfile) return false;

    if (tenantManagementRule === OWNER_TENANT_ALWAYS_ALLOWED) {
      return true;
    }

    if (tenantManagementRule === OWNER_TENANT_BALANCE_DEPENDENT) {
      return meterIsSettledOrInCredit;
    }

    return false;
  }, [isOwnerProfile, tenantManagementRule, meterIsSettledOrInCredit]);

  const tenantManagementRestriction = useMemo(() => {
    if (tenantManagementRule === OWNER_TENANT_NEVER_ALLOWED) {
      return "You are not permitted to add or edit the tenant for this meter.";
    }

    if (
      tenantManagementRule === OWNER_TENANT_BALANCE_DEPENDENT &&
      !meterIsSettledOrInCredit
    ) {
      return "Please settle the outstanding balance before adding or editing the tenant.";
    }

    return "";
  }, [tenantManagementRule, meterIsSettledOrInCredit]);

  async function searchTenantAccounts() {
    setTenantError("");
    setTenantResults([]);

    const q = tenantSearch.trim();
    if (q.length < 3) {
      setTenantError(
        "Enter at least 3 characters to search by email or mobile.",
      );
      return;
    }

    try {
      const r = await authenticatedFetch(
        `${API}/tenant-accounts/search/?q=${encodeURIComponent(q)}`,
        {
          headers: { Accept: "application/json" },
        },
      );
      const js = await r.json();

      if (!r.ok) throw new Error(js?.error || "Search failed.");

      setTenantResults(js.results || []);
    } catch (e) {
      setTenantError(String(e.message || e));
    }
  }
  async function saveTenant() {
    setTenantBusy(true);
    setTenantError("");
    setTenantMsg("");

    try {
      const requesterEmail = (
        localStorage.getItem("userEmail") ||
        JSON.parse(localStorage.getItem("profile") || "{}").email ||
        ""
      ).trim();

      const payload = selectedTenantAccount
        ? {
            selectedAccountId: selectedTenantAccount.accountId,
            requesterEmail,
          }
        : {
            ...tenantForm,
            requesterEmail,
          };

      const r = await authenticatedFetch(`${API}/meters/${meterId}/tenant/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const js = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(js?.error || "Failed to save tenant.");
      }

      setTenantMsg("Tenant linked successfully.");
      await load();

      setTenantForm({
        firstName: "",
        lastName: "",
        email: "",
        mobile: "",
      });
      setSelectedTenantAccount(null);
      setTenantSearch("");
      setTenantResults([]);
      setTimeout(() => {
        setTenantOpen(false);
        setTenantMsg("");
      }, 700);
    } catch (e) {
      setTenantError(String(e.message || e));
    } finally {
      setTenantBusy(false);
    }
  }

  useEffect(() => {
    if (meterProvider !== "p4power") return;
    if (!meter?.accountGuid) return;

    let alive = true;

    async function loadWalletBalance() {
      try {
        const r = await authenticatedFetch(
          `${API}/bc/customer-ledger/?account_id=${encodeURIComponent(meter.accountGuid)}`,
          {
            headers: { Accept: "application/json" },
          },
        );
        const js = await r.json();
        if (!alive || !js?.ok) return;

        const balance = Number(js.balance || 0);
        const rows = Array.isArray(js.entries) ? js.entries : [];

        const now = new Date();
        const start = new Date();
        start.setDate(now.getDate() - 29);

        let spendTotal = 0;
        const debitDates = [];

        for (const row of rows) {
          const d = row.postingDate ? new Date(row.postingDate) : null;
          const amt = Number(row.amount || 0);

          // Positive BC amounts are debit/bad spend.
          if (d && d >= start && d <= now && amt > 0) {
            spendTotal += amt;
            debitDates.push(d);
          }
        }

        let avgDaily = 0;

        if (spendTotal > 0 && debitDates.length > 0) {
          debitDates.sort((a, b) => a - b);

          const firstDebit = debitDates[0];

          const actualDays =
            Math.floor(
              (now.getTime() - firstDebit.getTime()) / (1000 * 60 * 60 * 24),
            ) + 1;

          const effectiveDays = Math.min(Math.max(actualDays, 1), 30);

          avgDaily = spendTotal / effectiveDays;
        }

        setWalletBalance(balance);
        setAvgSpendPerDayR(avgDaily);
      } catch (e) {
        console.error("Wallet load failed", e);
      }
    }

    loadWalletBalance();
    return () => {
      alive = false;
    };
  }, [meterProvider, meter?.accountGuid]);

  // ---- Take manual reading now ----------------------------------------------
  async function takeManualReadingNow() {
    setTakeBusy(true);
    setTakeErr("");
    setTakeMsg("");

    try {
      const userProfileId = getUserProfileId();

      if (!userProfileId) {
        throw new Error(
          "Could not identify your user profile. Please log in again.",
        );
      }

      if (takeCount >= takeLimit) {
        throw new Error(
          `Monthly limit reached: You’ve already taken ${takeLimit} readings for this meter this month.`,
        );
      }

      const res = await authenticatedFetch(
        `${API}/meters/${meterId}/readings/take/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // Note: Removed user_profile_id from body since backend derives it
          },
          body: JSON.stringify({
            type: "manual",
          }),
        },
      );

      const j = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(j?.message || j?.error || "Failed to take reading");
      }

      setTakeCount(Number(j.used || takeCount + 1));
      setTakeLimit(Number(j.limit || takeLimit));

      const capturedAt = j?.reading?.ts;

      setTakeMsg(
        capturedAt
          ? `Reading captured successfully at ${formatReadingDateTime(
              capturedAt,
            )}.`
          : "Reading captured successfully.",
      );
      await load();
      await loadManualReadingUsage();
    } catch (e) {
      setTakeErr(String(e.message || e));
    } finally {
      setTakeBusy(false);
    }
  }
  const isTOU = !!meter?.isTimeOfUse;

  // flag if either is Off for red styling
  const hardwareIssue = relayState === "No" || tamperState === "Yes";

  function downloadReadingsExcel() {
    const rows = filtered.map((r) => {
      const cons = consumptionById.get(r.id);

      return {
        Reading: r.value ?? "",
        Units: r.units || "",
        Consumption: Number.isFinite(cons) ? Number(cons.toFixed(2)) : "",
        Date: r.readingDate ? formatReadingDateTime(r.readingDate) : "",
        Notes: r.notes || "",
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);

    ws["!cols"] = [
      { wch: 15 },
      { wch: 12 },
      { wch: 15 },
      { wch: 25 },
      { wch: 60 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Meter Readings");

    XLSX.writeFile(wb, `meter-readings-${meter?.meter_number || meterId}.xlsx`);
  }

  const fullPageError = error || spError;
  const fullPageLoading = !fullPageError && (loading || spLoading);

  if (fullPageError) {
    return (
      <div className={styles.paymentStateScreen}>
        <div className={styles.paymentStateCard}>
          <div className={styles.paymentStateIcon}>!</div>

          <h2>Meter information unavailable</h2>
          <p>{fullPageError}</p>

          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => window.location.reload()}
          >
            Refresh and retry
          </button>

          <button
            type="button"
            className={styles.paymentStateBack}
            onClick={() => navigate("/meters")}
          >
            Return to meters
          </button>
        </div>
      </div>
    );
  }

  if (fullPageLoading) {
    return (
      <div className={styles.paymentStateScreen}>
        <div className={styles.paymentStateCard}>
          <div className={styles.paymentStateSpinner} />

          <h2>Loading meter information</h2>

          <p>We are loading the meter, readings and current meter status.</p>

          <strong>Please wait.</strong>
        </div>
      </div>
    );
  }

  if (!meter) {
    return (
      <div className={styles.paymentStateScreen}>
        <div className={styles.paymentStateCard}>
          <div className={styles.paymentStateIcon}>!</div>

          <h2>Meter unavailable</h2>
          <p>The requested meter could not be loaded.</p>

          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => navigate("/meters")}
          >
            Return to meters
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={`${styles.section} ${styles.meterDetailSection}`}>
        <div className={styles.meterDetailHero}>
          <div>
            <p className={styles.sectionEyebrow}>Meter</p>
            <h1 className={styles.h2}>Meter details</h1>
            <p className={styles.sectionIntro}>
              View the meter’s info, usage averages, wallet status, and recent
              readings.
            </p>
          </div>

          <div className={styles.meterDetailActions}>
            <Link className={styles.inlineBtn} to="/meters">
              Back to list
            </Link>

            {meter?.accountId && (
              <button
                className={styles.secondaryBtn}
                onClick={() => goToWalletFromMeter(meter)}
              >
                View wallet
              </button>
            )}

            {meter?.feeStructureFileName && (
              <a
                className={styles.secondaryBtn}
                href={`${API}/meters/${meterId}/fee-structure/`}
                download={meter.feeStructureFileName}
              >
                Download fee structure
              </a>
            )}

            {meter && showPay && (
              <button
                className={styles.primaryBtn}
                onClick={() => navigate(`/payment?meter=${meter.id}`)}
              >
                Fund account
              </button>
            )}

            {/* {meter && isOwnerProfile && (
              <button
                className={styles.secondaryBtn}
                disabled={!canManageTenant}
                title={
                  canManageTenant
                    ? "Add or edit tenant"
                    : tenantManagementRestriction
                }
                onClick={() => setTenantOpen(true)}
              >
                Add/Edit Tenant
              </button>
            )} */}
          </div>
        </div>

        {meter &&
          isOwnerProfile &&
          !canManageTenant &&
          tenantManagementRestriction && (
            <div className={styles.noteBox}>{tenantManagementRestriction}</div>
          )}

        {!loading && meter && (
          <>
            <div className={styles.meterDetailPanel}>
              <div className={styles.meterDetailPanelHeader}>
                <div>
                  <p className={styles.sectionEyebrow}>Overview</p>
                  <h2>Meter information</h2>
                </div>
              </div>

              <div className={styles.meterDetailGrid}>
                <div className={styles.meterDetailCard}>
                  <h3>Meter number</h3>
                  <p>{meter.meter_number || "—"}</p>
                </div>

                <div className={styles.meterDetailCard}>
                  <h3>Resident</h3>
                  <p>{meter.resident || "—"}</p>
                </div>

                <div className={styles.meterDetailCard}>
                  <h3>Account</h3>
                  <p>{meter.accountId || "—"}</p>
                </div>

                <div className={styles.meterDetailCard}>
                  <h3>Type</h3>
                  <p>{meter.utility_type || "—"}</p>
                </div>

                <div className={styles.meterDetailCard}>
                  <h3>Status</h3>
                  <p>{meter.status || "Active"}</p>
                </div>
                <div className={styles.meterDetailCard}>
                  <h3>Tariff</h3>
                  <p>
                    {rate > 0
                      ? `${fmtR(rate)} / kWh`
                      : isTOU
                        ? "Time-of-Use"
                        : "No tariff rate"}
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.meterDetailPanel}>
              <div className={styles.meterDetailPanelHeader}>
                <div>
                  <p className={styles.sectionEyebrow}>Usage</p>
                  <h2>Usage & credit averages</h2>
                </div>
              </div>

              <div className={styles.meterDetailGrid}>
                <div className={styles.meterDetailCard}>
                  <h3>Daily average</h3>
                  <p>{fmtK(dailyAvgKwh)}</p>
                </div>

                <div className={styles.meterDetailCard}>
                  <h3>Weekly average</h3>
                  <p>{fmtK(weeklyAvgKwh)}</p>
                </div>

                <div className={styles.meterDetailCard}>
                  <h3>Monthly average</h3>
                  <p>{fmtK(monthlyAvgKwh)}</p>
                </div>

                {displayBalance && (
                  <div className={styles.meterDetailCard}>
                    <h3>{displayBalance.label}</h3>
                    <p>{displayBalance.value}</p>
                  </div>
                )}

                <div className={styles.meterDetailCard}>
                  <h3>Days remaining</h3>
                  <p>
                    {estimatedDaysLeftFloor != null
                      ? estimatedDaysLeftFloor
                      : "—"}
                  </p>
                </div>

                <div className={styles.meterDetailCard}>
                  <h3>Estimated Remaining kWh</h3>
                  <p>{fmtK(estimatedRemainingKwh)}</p>
                </div>
              </div>
            </div>

            <div className={styles.meterDetailPanel}>
              <div className={styles.meterDetailPanelHeader}>
                <div>
                  <p className={styles.sectionEyebrow}>Analytics</p>
                  <h2>
                    {chartMode === "usage"
                      ? `Monthly usage — ${selectedYear}`
                      : chartMode === "reading"
                        ? `Accumulative reading — ${selectedYear}`
                        : `Daily consumption — ${currentMonthLabel}`}
                  </h2>
                </div>

                <div className={styles.meterChartActions}>
                  {chartMode !== "daily" && (
                    <select
                      className={styles.inlineBtn}
                      value={selectedYear ?? ""}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                      {years.map((y) => (
                        <option key={y}>{y}</option>
                      ))}
                    </select>
                  )}

                  {chartMode === "daily" && (
                    <select
                      className={styles.inlineBtn}
                      value={activeDailyMonth?.key || ""}
                      disabled={!availableDailyMonths.length}
                      onChange={(e) => setSelectedDailyMonth(e.target.value)}
                      aria-label="Select Daily Reading month"
                    >
                      {!availableDailyMonths.length ? (
                        <option value="">No months available</option>
                      ) : (
                        availableDailyMonths.map((month) => (
                          <option key={month.key} value={month.key}>
                            {month.label}
                          </option>
                        ))
                      )}
                    </select>
                  )}

                  <button
                    className={`${styles.secondaryBtn} ${
                      chartMode === "usage" ? styles.chartModeActive : ""
                    }`}
                    onClick={() => setChartMode("usage")}
                    aria-pressed={chartMode === "usage"}
                  >
                    Usage
                  </button>

                  <button
                    className={`${styles.secondaryBtn} ${
                      chartMode === "reading" ? styles.chartModeActive : ""
                    }`}
                    onClick={() => setChartMode("reading")}
                    aria-pressed={chartMode === "reading"}
                  >
                    Reading
                  </button>

                  <button
                    className={`${styles.secondaryBtn} ${
                      chartMode === "daily" ? styles.chartModeActive : ""
                    }`}
                    onClick={() => setChartMode("daily")}
                    aria-pressed={chartMode === "daily"}
                  >
                    Daily Reading
                  </button>

                  <button className={styles.inlineBtn} onClick={load}>
                    Refresh
                  </button>
                </div>
              </div>

              <div className={styles.meterChartCard}>
                {chartMode === "usage" ? (
                  <BarChart data={monthlyUsage} />
                ) : chartMode === "reading" ? (
                  <LineChart
                    dataA={accumulativeSeries.curr}
                    dataB={accumulativeSeries.prev}
                    labelA={selectedYear}
                    labelB={selectedYear - 1}
                  />
                ) : (
                  <DailyConsumptionLineChart
                    data={currentMonthDailyConsumption}
                  />
                )}
                {chartMode === "daily" &&
                  dailyConsumptionExtremes.highest &&
                  dailyConsumptionExtremes.lowest && (
                    <div className={styles.dailyConsumptionSummary}>
                      <div className={styles.dailyConsumptionSummaryCard}>
                        <span>Highest daily consumption</span>
                        <strong>
                          Day {dailyConsumptionExtremes.highest.x.getDate()} ·{" "}
                          {Number(
                            dailyConsumptionExtremes.highest.y,
                          ).toLocaleString("en-ZA", {
                            maximumFractionDigits: 2,
                          })}{" "}
                          {units}
                        </strong>
                      </div>

                      <div className={styles.dailyConsumptionSummaryCard}>
                        <span>Lowest daily consumption</span>
                        <strong>
                          Day {dailyConsumptionExtremes.lowest.x.getDate()} ·{" "}
                          {Number(
                            dailyConsumptionExtremes.lowest.y,
                          ).toLocaleString("en-ZA", {
                            maximumFractionDigits: 2,
                          })}{" "}
                          {units}
                        </strong>
                      </div>
                    </div>
                  )}
              </div>
            </div>

            <div className={styles.meterDetailPanel}>
              <div className={styles.meterDetailPanelHeader}>
                <div>
                  <p className={styles.sectionEyebrow}>Reading</p>
                  <h2>Take manual reading</h2>
                  <p className={styles.meterPanelText}>
                    {takeCount}/{takeLimit} readings used this month
                  </p>
                </div>

                <button
                  className={styles.primaryBtn}
                  onClick={takeManualReadingNow}
                  disabled={takeBusy || takeCount >= takeLimit}
                >
                  {takeBusy ? "Capturing…" : "Take reading"}
                </button>
              </div>

              {takeErr && <div className={styles.noteBox}>{takeErr}</div>}
              {takeMsg && <div className={styles.noteBox}>{takeMsg}</div>}
            </div>

            <div className={styles.meterDetailPanel}>
              <div className={styles.meterDetailPanelHeader}>
                <div>
                  <p className={styles.sectionEyebrow}>History</p>
                  <h2>Recent readings</h2>
                </div>

                <input
                  className={`${styles.authInput} ${styles.meterNotesFilter}`}
                  placeholder="Filter notes"
                  value={qNotes}
                  onChange={(e) => setQNotes(e.target.value)}
                />
                <button
                  className={styles.secondaryBtn}
                  type="button"
                  onClick={downloadReadingsExcel}
                  disabled={!filtered.length}
                >
                  Download Excel
                </button>
              </div>

              <div className={styles.meterReadingsTableCard}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Reading</th>
                      <th>Units</th>
                      <th>Consumption</th>
                      <th>Date</th>
                      <th>Notes</th>
                    </tr>
                  </thead>

                  <tbody>
                    {pageItems.map((r) => {
                      const cons = consumptionById.get(r.id);

                      return (
                        <tr key={r.id}>
                          <td>{r.value}</td>
                          <td>{r.units || "—"}</td>
                          <td>
                            {Number.isFinite(cons)
                              ? `${cons.toFixed(2)} ${r.units}`
                              : "—"}
                          </td>
                          <td>{formatReadingDateTime(r.readingDate)}</td>
                          <td>{r.notes || "—"}</td>
                        </tr>
                      );
                    })}

                    {!pageItems.length && (
                      <tr>
                        <td colSpan="5">No readings found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className={styles.meterPagination}>
                <button
                  className={styles.inlineBtn}
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </button>

                <span className={styles.meterPanelText}>
                  {rangeStart}–{rangeEnd} of {filtered.length} readings
                </span>

                <button
                  className={styles.inlineBtn}
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
        {tenantOpen && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalCard}>
              <div className={styles.modalHeader}>
                <div>
                  <p className={styles.sectionEyebrow}>Tenant</p>
                  <h2>Add/Edit Tenant</h2>
                  <p className={styles.sectionIntro}>
                    Current resident:{" "}
                    {meter?.resident ||
                      meter?.residentAccountName ||
                      "No resident linked"}
                  </p>
                </div>

                <button
                  className={styles.inlineBtn}
                  onClick={() => setTenantOpen(false)}
                  disabled={tenantBusy}
                >
                  Close
                </button>
              </div>

              {tenantError && (
                <div className={styles.noteBox}>{tenantError}</div>
              )}
              {tenantMsg && (
                <div className={styles.successBox}>{tenantMsg}</div>
              )}

              <div className={styles.formGroup}>
                <label>Search existing account by email or mobile</label>
                <div className={styles.inlineFormRow}>
                  <input
                    value={tenantSearch}
                    onChange={(e) => setTenantSearch(e.target.value)}
                    placeholder="email@example.com or 082..."
                  />
                  <button
                    className={styles.tenantSearchBtn}
                    type="button"
                    onClick={searchTenantAccounts}
                  >
                    Search
                  </button>
                </div>
              </div>

              {tenantResults.length > 0 && (
                <div className={styles.tenantResultList}>
                  {tenantResults.map((a) => {
                    // Normalise field names — adjust these to match YOUR API
                    const acctName =
                      a.name || a.accountName || a.fullName || a.resident || "";
                    const acctEmail =
                      a.email || a.account_email || a.profile?.email || "";
                    const acctMobile =
                      a.mobile ||
                      a.mobile_number ||
                      a.phone ||
                      a.account_mobile ||
                      "";
                    const acctId = a.accountId || a.id || a.account_guid || "";

                    return (
                      <button
                        key={acctId}
                        type="button"
                        className={
                          selectedTenantAccount?.accountId === acctId
                            ? styles.tenantResultActive
                            : styles.tenantResult
                        }
                        onClick={() => {
  if (selectedTenantAccount?.accountId === acctId) {
    setSelectedTenantAccount(null);
    setTenantForm({ firstName: "", lastName: "", email: "", mobile: "" });
    setAutofilledFields({ firstName: false, lastName: false, email: false, mobile: false });
  } else {
    setSelectedTenantAccount(a);

    const [first, ...rest] = acctName.split(" ");
    const fn = a.firstName || a.first_name || first || "";
    const ln = a.lastName  || a.last_name  || rest.join(" ") || "";

    setTenantForm({
      firstName: fn,
      lastName: ln,
      email: acctEmail,
      mobile: acctMobile,
    });

    // Snapshot which fields were already filled by the search result
    setAutofilledFields({
      firstName: !!fn,
      lastName: !!ln,
      email: !!acctEmail,
      mobile: !!acctMobile,
    });
  }
}}
                      >
                        <strong>{acctName || "Unnamed account"}</strong>
                        <span>
                          {acctEmail || "No email"} •{" "}
                          {acctMobile || "No mobile"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className={styles.modalDivider}>Or create a new tenant</div>

              <div className={styles.formGridTwo}>
                <div className={styles.formGroup}>
                  <label>First name</label>
                  <input
  value={tenantForm.firstName}
  disabled={autofilledFields.firstName}
  onChange={(e) => setTenantForm((p) => ({ ...p, firstName: e.target.value }))}
/>
                </div>

                <div className={styles.formGroup}>
                  <label>Last name</label>
                  <input
  value={tenantForm.lastName}
  disabled={autofilledFields.lastName}
  onChange={(e) => setTenantForm((p) => ({ ...p, lastName: e.target.value }))}
/>
                </div>

                <div className={styles.formGroup}>
                  <label>Email</label>
                  <input
  value={tenantForm.email}
  disabled={autofilledFields.email}
  onChange={(e) => setTenantForm((p) => ({ ...p, email: e.target.value }))}
/>
                </div>

                <div className={styles.formGroup}>
                  <label>Mobile</label>
                  <input
  value={tenantForm.mobile}
  disabled={autofilledFields.mobile}
  onChange={(e) => setTenantForm((p) => ({ ...p, mobile: e.target.value }))}
/>
                </div>
              </div>

              <div className={styles.modalActions}>
                <button
                  className={styles.primaryBtn}
                  type="button"
                  disabled={tenantBusy}
                  onClick={saveTenant}
                >
                  {tenantBusy ? "Saving..." : "Save tenant"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
